import React, { useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { useApplyPlanEditDetails } from "../../../hooks";
import { useToast } from "@shared/ui/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { AlertCircle } from "lucide-react";

const Review: React.FC<any> = ({
  allStepData,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const selectedRecords = allStepData?.[0]?.selectedRecords || [];
  const payload = allStepData?.[2]?.payload;

  const { mutateAsync: applyPlanEditDetails, isPending: isApplying } = useApplyPlanEditDetails();
  const { toast } = useToast();

  const handleApply = async () => {
    if (!payload) {
      toast({
        title: "Error",
        description: "Missing update payload. Please go back and regenerate the preview.",
        variant: "destructive"
      });
      return;
    }

    try {
      await applyPlanEditDetails(payload);
      onNext?.();
    } catch (error: any) {
      console.error("Apply failed", error);
      toast({
        title: "Update Failed",
        description: error?.message || "Failed to apply plan edits.",
        variant: "destructive"
      });
    }
  };
  
  // Extract API response
  const previewResult = allStepData?.[2]?.previewData?.result || {};
  
  const recordsCount = previewResult.recordsCount || previewResult.records_count || selectedRecords.length || 0;
  const fieldsChangedCount = previewResult.fieldsChangedCount || previewResult.fields_changed_count || 0;
  const fieldsChanged = previewResult.fieldsChanged || previewResult.fields_changed || [];
  const structuralChangesCount = previewResult.structuralChangesCount || previewResult.structural_changes_count || 0;
  const structuralLabels = previewResult.structuralLabels || previewResult.structural_labels || [];

  const previewRows = previewResult.previewRows || [];
  const tariffPreview = previewResult.tariffPreview || previewResult.tariff_preview;
  const chargePreview = previewResult.chargePreview || previewResult.charge_preview;

  const chargeOp = payload?.operations?.add_a_charge;
  const hasCharge = !!chargeOp || (!!chargePreview && !!(chargePreview.sectionDisplay || chargePreview.section_display || chargePreview.hasChargeChange));

  const sectionDisplay = chargePreview?.sectionDisplay || chargePreview?.section_display || "Charge";

  let chargeName = chargePreview?.chargeName || chargePreview?.charge_name;
  if (!chargeName && chargeOp) {
    chargeName = chargeOp.charge_data?.name;
  }

  let chargeValueDisplay = chargePreview?.chargeValueDisplay || chargePreview?.charge_value_display;
  if (!chargeValueDisplay && chargeOp) {
    const cd = chargeOp.charge_data;
    if (cd?.amount !== undefined) {
      chargeValueDisplay = String(cd.amount);
    } else if (cd?.rate !== undefined) {
      chargeValueDisplay = `${cd.rate}%`;
    } else if (cd?.usage !== undefined && cd?.charge_rate !== undefined) {
      chargeValueDisplay = `${cd.usage}% @ ${cd.charge_rate}/unit`;
    } else {
      chargeValueDisplay = "-";
    }
  }

  const fieldChangesData = useMemo(() => previewRows, [previewRows]);

  const formatValue = (val: any): string => {
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    if (val === null || val === undefined) return "-";
    return String(val);
  };

  const renderDiff = (diffObj: any) => {
    if (!diffObj) return <span className="text-gray-400">-</span>;
    
    let oldVal = formatValue(diffObj.old);
    let newVal = formatValue(diffObj.new);

    if (oldVal === newVal || oldVal === "-") return <span>{newVal}</span>;
    
    return (
      <>
        <span className="text-red-500 line-through mr-2">{oldVal}</span>
        <span className="text-green-500">{newVal}</span>
      </>
    );
  };

  const fieldChangesColumns: TableColumn<any>[] = [
    { key: "planCode", header: "Code" },
    { key: "planName", header: "Name" },
    // { key: "status", header: "Status", render: (_, row) => renderDiff(row.diff?.status) },
    { key: "category", header: "Category", render: (_, row) => renderDiff(row.diff?.categories || row.diff?.category) },
    { key: "subcategory", header: "Sub Category", render: (_, row) => renderDiff(row.diff?.subCategories || row.diff?.sub_categories) },
    { key: "effective", header: "Effective From", render: (_, row) => renderDiff(row.diff?.startDate || row.diff?.start_date) },
    { key: "end_date", header: "Validate To", render: (_, row) => renderDiff(row.diff?.validity) },
  ];

  const tariffColumns: TableColumn<any>[] = [
    { key: "planName", header: "Plan" },
    { key: "currentRateDisplay", header: "Current Tariff", render: (val) => <span className="text-gray-500">{val || "None"}</span> },
    { key: "newRateDisplay", header: "New Tariff", render: (val) => <span className="text-blue-600">{val || "None"}</span> },
  ];
  
  const tariffData = useMemo(() => {
    return tariffPreview?.rows || [];
  }, [tariffPreview]);


  return (
    <div className="space-y-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Review & Apply</h2>
        <p className="text-sm text-gray-500">Confirm the changes below. They will be applied to {selectedRecords.length || 1} plan{(selectedRecords.length || 1) !== 1 ? 's' : ''} when you click Apply.</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-xs text-blue-600 font-semibold mb-1 tracking-wider">Records</div>
          <div className="text-2xl font-bold text-blue-900">{recordsCount}</div>
          <div className="text-sm text-blue-700">plans selected</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 overflow-hidden">
          <div className="text-xs text-emerald-600 font-semibold mb-1 tracking-wider">Fields Changed</div>
          <div className="text-2xl font-bold text-emerald-900">{fieldsChangedCount}</div>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-sm text-emerald-700 truncate cursor-pointer">
                  {fieldsChanged.map(f => f.replace(/_/g, ' ')).join(', ') || "None"}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white border shadow-lg p-3 z-50 whitespace-normal break-words text-emerald-700">
                {fieldsChanged.map(f => f.replace(/_/g, ' ')).join(', ') || "None"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 overflow-hidden">
          <div className="text-xs text-yellow-600 font-semibold mb-1 tracking-wider">Structural Changes</div>
          <div className="text-2xl font-bold text-yellow-900">{structuralChangesCount}</div>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-sm text-yellow-700 truncate cursor-pointer">
                  {structuralLabels.map(l => l.replace(/_/g, ' ')).join(', ') || "None"}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white border shadow-lg p-3 z-50 whitespace-normal break-words text-yellow-700">
                {structuralLabels.map(l => l.replace(/_/g, ' ')).join(', ') || "None"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="space-y-6">
        {/* Field Changes Table */}
        <div className="bg-white border rounded-lg p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Field Changes</h3>
          <GenericTable
            data={fieldChangesData}
            columns={fieldChangesColumns}
            rowKey="planId"
          />
        </div>

        {/* Tariff Replacement Preview */}
        {tariffPreview && (tariffPreview.hasTariffChange) && (
          <div className="bg-white border rounded-lg p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Tariff Replacement Preview</h3>
            <p className="text-xs text-gray-500 mb-4">
              For service {tariffPreview.service}, the existing tariff of each plan will be replaced with <strong>{tariffPreview.newRateDisplay || tariffPreview.new_rate_display}</strong>
            </p>
            <GenericTable
              data={tariffData}
              columns={tariffColumns}
              rowKey="planId"
            />
          </div>
        )}

        {/* New Charge Preview */}
        {hasCharge && (
          <div className="bg-white border rounded-lg p-4 shadow-sm flex flex-col items-start">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">New Charge Preview</h3>
            <p className="text-xs text-gray-500 mb-4">A new <strong>{sectionDisplay}</strong> entry will be added to every selected plan.</p>
            <div className="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md text-sm font-medium">
              <span className="mr-2">+ {chargeName}</span>
              <span className="bg-green-200 text-green-800 px-1.5 py-0.5 rounded text-xs">{chargeValueDisplay}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between space-x-3 mt-8 border-t pt-6">
        {!isFirstStep && (
          <Button
             variant="outline"
            onClick={onPrevious}
          >
            Previous
          </Button>
        )}
        <Button
           variant="default"
           onClick={handleApply}
           disabled={isApplying}
           className=" ml-auto"
        >
          {isApplying ? "Applying..." : "Apply"}
        </Button>
      </div>
    </div>
  );
};

export default Review;
